/**
 * The rail's time sections (owner 2026-10-31): "Recent" holds the last three days; older conversations fall into
 * their week, and a week busier than three conversations splits into one section per day, so a quiet week is one
 * short list and a busy one stays readable. Weeks start on Monday; days are the viewer's local days.
 */

/** "Recent" covers today and the days before it, this many days in all. */
export const RECENT_DAYS = 3;
/** A week with more conversations than this is shown day by day. */
export const DAY_SPLIT_THRESHOLD = 3;

export interface TimeSection<T> {
  /** Stable across renders: `recent`, `day:<yyyy-mm-dd>` or `week:<yyyy-mm-dd of its Monday>`. */
  key: string;
  label: string;
  rows: T[];
}

function startOfDay(date: Date): Date {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  // getDay(): Sunday 0 … Saturday 6; Monday starts the week.
  day.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return day;
}

function dateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Sections for rows ordered newest first (the list's own order is kept inside each section). `at` reads the moment a
 * row is filed under (its last activity).
 */
export function conversationTimeSections<T>(
  rows: readonly T[],
  at: (row: T) => Date,
  now: Date = new Date(),
  locale?: string,
): TimeSection<T>[] {
  const recentFrom = startOfDay(now);
  recentFrom.setDate(recentFrom.getDate() - (RECENT_DAYS - 1));

  const recent: T[] = [];
  const weeks = new Map<string, { start: Date; rows: T[] }>();
  for (const row of rows) {
    const when = at(row);
    if (Number.isNaN(when.getTime()) || when >= recentFrom) {
      recent.push(row);
      continue;
    }
    const start = startOfWeek(when);
    const key = dateKey(start);
    const week = weeks.get(key) ?? { start, rows: [] };
    week.rows.push(row);
    weeks.set(key, week);
  }

  const showYear = (date: Date) => date.getFullYear() !== now.getFullYear();
  const dayLabel = (date: Date) =>
    date.toLocaleDateString(locale, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      ...(showYear(date) ? { year: 'numeric' } : {}),
    });
  const shortLabel = (date: Date, withYear: boolean) =>
    date.toLocaleDateString(locale, { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) });

  const sections: TimeSection<T>[] = recent.length > 0 ? [{ key: 'recent', label: 'Recent', rows: recent }] : [];
  const ordered = [...weeks.values()].sort((a, b) => b.start.getTime() - a.start.getTime());
  for (const week of ordered) {
    if (week.rows.length > DAY_SPLIT_THRESHOLD) {
      const days = new Map<string, { date: Date; rows: T[] }>();
      for (const row of week.rows) {
        const date = startOfDay(at(row));
        const key = dateKey(date);
        const day = days.get(key) ?? { date, rows: [] };
        day.rows.push(row);
        days.set(key, day);
      }
      for (const day of [...days.values()].sort((a, b) => b.date.getTime() - a.date.getTime())) {
        sections.push({ key: `day:${dateKey(day.date)}`, label: dayLabel(day.date), rows: day.rows });
      }
      continue;
    }
    const end = new Date(week.start);
    end.setDate(end.getDate() + 6);
    const withYear = showYear(week.start) || showYear(end);
    sections.push({
      key: `week:${dateKey(week.start)}`,
      label: `${shortLabel(week.start, withYear)} – ${shortLabel(end, withYear)}`,
      rows: week.rows,
    });
  }
  return sections;
}
